import { useEffect, useRef, useState, useCallback } from 'react';

// Animation timing constants
export const ANIMATION_DURATION = {
  fast: 0.3,
  normal: 0.5,
  slow: 0.8,
};

export const STAGGER_INCREMENT = 0.08;

export const ANIMATION_EASING = [0.25, 0.1, 0.25, 1]; // ease-out-quart

interface UseScrollAnimationOptions {
  threshold?: number | number[];
  rootMargin?: string;
  triggerOnce?: boolean;
  trackProgress?: boolean;
}

interface UseScrollAnimationReturn {
  ref: React.RefObject<HTMLDivElement>;
  isVisible: boolean;
  hasAnimated: boolean;
  scrollProgress: number; // 0-1 as element moves through viewport
  scrollDirection: 'up' | 'down' | null;
}

/**
 * Enhanced hook for scroll-triggered animations using Intersection Observer
 * - Detects when an element enters/exits the viewport
 * - Tracks scroll progress through viewport (0 = just entered, 1 = about to exit)
 * - Detects scroll direction
 */
export const useScrollAnimation = (
  options: UseScrollAnimationOptions = {}
): UseScrollAnimationReturn => {
  const {
    threshold = 0.15,
    rootMargin = '0px',
    triggerOnce = false,
    trackProgress = false,
  } = options;

  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | null>(null);
  const lastScrollY = useRef(0);

  // Track scroll direction
  useEffect(() => {
    if (!trackProgress) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current) {
        setScrollDirection('down');
      } else if (currentScrollY < lastScrollY.current) {
        setScrollDirection('up');
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackProgress]);

  // Track scroll progress through element
  const updateProgress = useCallback(() => {
    if (!ref.current || !trackProgress) return;

    const rect = ref.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Calculate progress: 0 when element enters bottom, 1 when exits top
    const elementTop = rect.top;
    const elementHeight = rect.height;

    // Progress calculation
    const start = windowHeight; // Element starts below viewport
    const end = -elementHeight; // Element ends above viewport
    const current = elementTop;

    const progress = 1 - (current - end) / (start - end);
    setScrollProgress(Math.max(0, Math.min(1, progress)));
  }, [trackProgress]);

  useEffect(() => {
    if (!trackProgress) return;

    window.addEventListener('scroll', updateProgress, { passive: true });
    updateProgress(); // Initial calculation

    return () => window.removeEventListener('scroll', updateProgress);
  }, [trackProgress, updateProgress]);

  // Intersection Observer for visibility
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setHasAnimated(true);
        } else if (!triggerOnce && hasAnimated) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce, hasAnimated]);

  return { ref, isVisible, hasAnimated, scrollProgress, scrollDirection };
};

/**
 * Hook specifically for staggered children animations
 */
export const useStaggeredAnimation = (
  itemCount: number,
  options: UseScrollAnimationOptions = {}
) => {
  const { ref, isVisible, hasAnimated, scrollProgress, scrollDirection } = useScrollAnimation(options);

  const getStaggerDelay = useCallback((index: number) => {
    return index * STAGGER_INCREMENT;
  }, []);

  return {
    ref,
    isVisible,
    hasAnimated,
    scrollProgress,
    scrollDirection,
    getStaggerDelay,
    itemCount,
  };
};

export default useScrollAnimation;
